import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { getMyReportActions, prioritizeMyAction } from '../api/dashboardApi'
import { isQueryReady, type DashboardQuery, type MyActionSelection } from '../types'
import { DASHBOARD_REFRESH_MS, queryKeyParts } from './useActionDashboard'

/**
 * "Aksiyon Beklenen Maddeler" tablosunun sayfa boyu (Raporum revizyonu §7.3 + mockup: "1 - 5 / 12").
 * Kullanıcı mockup'taki seçiciyle büyütebilir; sunucu üst sınırı ayrıca uygular.
 */
export const MY_ACTIONS_PAGE_SIZE = 5

/** Satır/sayfa seçeneği (mockup'taki "5 / sayfa" kontrolü). */
export const MY_ACTIONS_PAGE_SIZES = [5, 10, 20]

/**
 * Raporum'un **alt tablosunun** sunucu-durumu (Raporum revizyonu §7) — panonun kendi sorgusundan
 * **ayrı**.
 *
 * <p>Ayrı olmasının sebebi maliyet değil davranış: bir dilime/bara tıklamak, sayfa ya da sıralama
 * değiştirmek yalnız bu listeyi yeniler; donut'lar, kalan süre barları ve "Son Güncelleme" damgası
 * oldukları gibi kalır. Aynı sorguya bağlansaydı her tıklama tüm panoyu yükleme durumuna düşürürdü
 * (Yönetici Raporu'nda 077 §K4 ile verilen kararın aynısı).
 *
 * <p>`i18n.language` anahtarın parçasıdır: satırın dile duyarlı alanları sunucuda `Accept-Language`'e
 * göre çözülür → dil değişince önbellekteki eski dildeki yanıt sunulmamalı.
 *
 * <p>`placeholderData` **kullanılmaz**: süzgeç/sayfa değiştiğinde eski satırları yeni süzgece aitmiş gibi
 * göstermek yanıltıcı olurdu. Yenileme **hatasında** TanStack Query son başarılı veriyi ekranda tutar.
 */
export function useMyReportActions(
  query: DashboardQuery,
  selection: MyActionSelection,
  page: number,
  size: number,
  sort: string | undefined,
  enabled = true,
) {
  const { i18n } = useTranslation()
  return useQuery({
    queryKey: [
      'my-report-actions',
      i18n.language,
      selection.scope,
      selection.bucket,
      selection.remaining ?? 'all',
      page,
      size,
      sort ?? 'default',
      ...queryKeyParts(query),
    ],
    queryFn: () => getMyReportActions(query, selection, page, size, sort),
    enabled: enabled && isQueryReady(query),
    refetchOnWindowFocus: false,
    // Pano ile AYNI aralık (2026-08-19): ayrışırsa donut'lar ile hemen altındaki tablo farklı anların
    // verisini gösterir. Elle "Yenile" butonu bu yüzden gereksizleşti ve kaldırıldı.
    refetchInterval: DASHBOARD_REFRESH_MS,
    // Periyodik yoklama merkezî hata hunisinden (019) MUAFTIR: kullanıcı isteğiyle değil, arka planda
    // 30 saniyede bir çalışır ve başarısızlığın kullanıcıya görünen yüzeyi zaten satır-içi hata bandıdır
    // (`home-error`). Susturulmasaydı bir kesintide pano açık kaldığı sürece HER 30 SANİYEDE bir hata
    // toast'ı düşerdi — üstelik bandın üstüne. Aynı gerekçe ve aynı desen: `useUnreadCount` (058).
    meta: { suppressGlobalError: true },
  })
}

/**
 * "Önceliklendir" gönderimi (§7.4).
 *
 * <p><b>Hiçbir sorgu invalidate edilMEZ:</b> önceliklendirme aksiyonu değiştirmez, yalnız bildirim
 * kuyruğuna satır yazar → listenin, donut'ların ya da sayıların yenilenmesi için bir sebep yoktur.
 * Kullanıcının kendi bildirim rozeti de etkilenmez (alıcı aksiyonun sorumlusudur, gönderen değil).
 */
export function useMyActionPrioritize() {
  return useMutation({ mutationFn: (actionId: number) => prioritizeMyAction(actionId) })
}
